/**
 * "AI Agent" tab — internal clinical-ops assistant. Left: the calling
 * admin's own threads. Right: the selected thread's chat + any
 * confirmation cards for proposed write actions. Thread selection lives in
 * the URL (`?thread=`) so it's shareable/refresh-safe.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  useAgentThread,
  useAgentThreads,
  useCreateAgentThread,
  useDeleteAgentThread,
  useRenameAgentThread,
  useSendAgentMessage,
} from '@/lib/api/clinicalAdminHooks';
import type { AgentMessage, AgentThreadSummary } from '@/types/clinicalAdmin';

import { ConfirmActionCard } from './ConfirmActionCard';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  fmtDateTime,
} from './ui';

function displayText(m: AgentMessage): string {
  if (m.content_text && m.content_text.trim()) return m.content_text;
  if (typeof m.content === 'string') return m.content;
  return '';
}

export function AgentScreen() {
  const [params, setParams] = useSearchParams();
  const threadId = params.get('thread');
  const [threadSearch, setThreadSearch] = useState('');
  const [debouncedThreadSearch, setDebouncedThreadSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedThreadSearch(threadSearch.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [threadSearch]);

  const threadsQ = useAgentThreads(debouncedThreadSearch);
  const createThread = useCreateAgentThread();

  const selectThread = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('thread', id);
    else next.delete('thread');
    setParams(next, { replace: true });
  };

  const onNewThread = async () => {
    const res = await createThread.mutateAsync();
    selectThread(res.thread_id);
  };

  const rename = useRenameAgentThread();
  const del = useDeleteAgentThread();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const saveRename = (id: string) => {
    const title = editTitle.trim();
    setEditingId(null);
    if (title) rename.mutate({ threadId: id, title });
  };

  const onDeleteThread = (t: AgentThreadSummary) => {
    if (
      !window.confirm(
        `Delete thread "${t.title || 'Untitled'}"?\n\nThis permanently removes the conversation and any pending actions.`,
      )
    )
      return;
    del.mutate(t.id, {
      onSuccess: () => {
        if (t.id === threadId) selectThread(null);
      },
    });
  };

  return (
    <div className="flex gap-16 items-start">
      {/* Thread sidebar */}
      <div className="w-[260px] shrink-0">
        <Card
          title={`Threads (${threadsQ.data?.length ?? 0})`}
          action={
            <button
              type="button"
              onClick={onNewThread}
              disabled={createThread.isPending}
              className="h-26 px-10 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40"
            >
              + New
            </button>
          }
        >
          <div className="px-10 pb-10">
            <div className="relative">
              <Search
                aria-hidden
                className="absolute left-9 top-1/2 h-14 w-14 -translate-y-1/2 text-ink-muted"
              />
              <input
                type="search"
                value={threadSearch}
                onChange={(e) => setThreadSearch(e.target.value)}
                placeholder="Search orders, names, meds…"
                className="h-34 w-full rounded border border-line bg-white pl-30 pr-30 type-body-sm outline-none focus:border-ink"
              />
              {threadSearch ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setThreadSearch('')}
                  className="absolute right-7 top-1/2 -translate-y-1/2 rounded p-3 text-ink-muted hover:bg-sunken hover:text-ink"
                >
                  <X aria-hidden className="h-13 w-13" />
                </button>
              ) : null}
            </div>
          </div>
          {threadsQ.isLoading ? (
            <LoadingState label="Loading threads…" />
          ) : threadsQ.isError ? (
            <ErrorState>Could not load threads.</ErrorState>
          ) : (threadsQ.data?.length ?? 0) === 0 ? (
            <EmptyState>
              {debouncedThreadSearch ? 'No matching threads.' : 'No threads yet. Start one.'}
            </EmptyState>
          ) : (
            <div className="flex flex-col">
              {threadsQ.data!.map((t) => (
                <div
                  key={t.id}
                  className={`group flex items-stretch border-t border-line-subtle ${
                    t.id === threadId ? 'bg-sunken' : 'hover:bg-sunken/60'
                  }`}
                >
                  {editingId === t.id ? (
                    <input
                      autoFocus
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveRename(t.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      onBlur={() => saveRename(t.id)}
                      className="flex-1 min-w-0 type-label-sm px-10 py-8 m-4 bg-white border border-line rounded"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => selectThread(t.id)}
                      className="flex-1 min-w-0 text-left px-12 py-10"
                    >
                      <div className="type-label-sm truncate">
                        {t.title || 'Untitled'}
                      </div>
                      <div className="flex items-center gap-6 mt-2">
                        <span className="type-10 text-ink-muted">
                          {fmtDateTime(t.updated_at)}
                        </span>
                        {t.pending_count > 0 ? (
                          <Badge tone="warn">{t.pending_count} pending</Badge>
                        ) : null}
                      </div>
                    </button>
                  )}
                  {editingId !== t.id ? (
                    <div className="flex items-center gap-1 pr-6 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <button
                        type="button"
                        title="Rename"
                        aria-label="Rename thread"
                        onClick={() => {
                          setEditingId(t.id);
                          setEditTitle(t.title || '');
                        }}
                        className="p-1 rounded text-ink-muted hover:text-ink hover:bg-raised"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-4 h-4"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete thread"
                        onClick={() => onDeleteThread(t)}
                        className="p-1 rounded text-ink-muted hover:text-danger hover:bg-raised"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-4 h-4"
                        >
                          <path d="M3 6h18" />
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Chat panel */}
      <div className="flex-1 min-w-0">
        {threadId ? (
          <ChatPanel threadId={threadId} />
        ) : (
          <Card title="Clinical-ops agent">
            <div className="px-15 py-30 type-body-sm text-ink-muted">
              Select a thread or start a new one. Ask things like{' '}
              <span className="font-mono">
                “SYS-3CBA7548 shows an eRx but the pharmacy hasn’t received it —
                what’s going on?”
              </span>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

export function ChatPanel({
  threadId,
  initialDraft = '',
}: {
  threadId: string;
  initialDraft?: string;
}) {
  const threadQ = useAgentThread(threadId);
  const send = useSendAgentMessage(threadId);
  const [input, setInput] = useState('');
  const [opus, setOpus] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (initialDraft.trim()) setInput((current) => current || initialDraft);
  }, [initialDraft, threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [threadQ.data?.messages.length, send.isPending]);

  const onSend = () => {
    const content = input.trim();
    if (!content || send.isPending) return;
    setInput('');
    send.mutate({ content, model: opus ? 'opus' : null });
  };

  const messages = (threadQ.data?.messages ?? []).filter(
    (m) => m.role !== 'tool' && displayText(m).trim(),
  );
  const allActions = threadQ.data?.pending_actions ?? [];
  // Pending = actionable (full card). Everything else (executed/rejected/
  // failed/expired) is terminal — tucked into a collapsed "Past actions"
  // disclosure so it doesn't pile up.
  const actionable = allActions.filter((a) => a.status === 'pending');
  const history = allActions.filter((a) => a.status !== 'pending');

  return (
    <Card title="Clinical-ops agent">
      <div className="flex flex-col gap-12 p-12">
        <div
          ref={scrollRef}
          className="flex flex-col gap-12 max-h-[55vh] overflow-y-auto"
        >
          {threadQ.isLoading ? (
            <LoadingState label="Loading…" />
          ) : threadQ.isError ? (
            <ErrorState>Could not load thread.</ErrorState>
          ) : messages.length === 0 ? (
            <EmptyState>Ask about an order, patient, or SYS- number.</EmptyState>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex flex-col gap-2 ${
                  m.role === 'user' ? 'items-end' : 'items-start'
                }`}
              >
                <Badge tone={m.role === 'user' ? 'neutral' : 'accent'}>
                  {m.role}
                </Badge>
                <div
                  className={`type-body-sm rounded px-12 py-8 max-w-[90%] ${
                    m.role === 'user'
                      ? 'bg-sunken whitespace-pre-wrap'
                      : 'bg-raised border border-line-subtle [&_a]:text-accent [&_a]:underline [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_table]:my-2 [&_table]:w-full [&_th]:text-left [&_th]:pr-3 [&_th]:py-1 [&_td]:pr-3 [&_td]:py-1 [&_td]:align-top [&_code]:bg-sunken [&_code]:px-1 [&_code]:rounded [&_h1]:type-label-md [&_h2]:type-label-md [&_h3]:font-semibold [&_strong]:font-semibold'
                  }`}
                >
                  {m.role === 'user' ? (
                    displayText(m)
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {displayText(m)}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))
          )}
          {send.isPending ? (
            <div className="type-label-sm text-ink-muted">Thinking…</div>
          ) : null}
        </div>

        {/* Past (executed/rejected/…) actions — collapsed so they don't pile up */}
        {history.length > 0 ? (
          <div className="flex flex-col gap-8">
            <button
              type="button"
              onClick={() => setHistoryOpen((v) => !v)}
              className="self-start flex items-center gap-6 type-label-sm text-ink-muted hover:text-ink"
            >
              <span
                aria-hidden
                className={`transition-transform ${historyOpen ? 'rotate-90' : ''}`}
              >
                ▸
              </span>
              Past actions ({history.length})
            </button>
            {historyOpen ? (
              <div className="flex flex-col gap-8">
                {history.map((a) => (
                  <ConfirmActionCard
                    key={a.id}
                    action={a}
                    threadId={threadId}
                    collapsible
                  />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Pending confirmations — actionable, always expanded */}
        {actionable.length > 0 ? (
          <div className="flex flex-col gap-12">
            {actionable.map((a) => (
              <ConfirmActionCard key={a.id} action={a} threadId={threadId} />
            ))}
          </div>
        ) : null}

        {send.isError ? (
          <div className="type-label-sm text-danger">
            {send.error instanceof Error ? send.error.message : 'Send failed.'}
          </div>
        ) : null}

        {/* Composer */}
        <div className="flex flex-col gap-8 border-t border-line pt-12">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder="Ask about an order / patient… (Enter to send, Shift+Enter for newline)"
            rows={2}
            className="type-body-sm px-12 py-10 rounded border border-line bg-white w-full"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-6 type-label-sm text-ink-muted">
              <input
                type="checkbox"
                checked={opus}
                onChange={(e) => setOpus(e.target.checked)}
              />
              Deep reasoning (Opus)
            </label>
            <button
              type="button"
              onClick={onSend}
              disabled={!input.trim() || send.isPending}
              className="h-30 px-16 bg-ink text-page type-label-sm rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
